import React from 'react';
import { Progress, Tooltip, Button, Space } from 'antd';
import {
  ThunderboltOutlined, CrownOutlined, GiftOutlined, LoginOutlined,
  WarningOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ChatQuota } from '@/stores/chat';

const PLAN_LABEL: Record<string, string> = {
  free: '免费版',
  pro: '专业版',
  max: '旗舰版',
  team: '团队版',
};

interface QuotaBarProps {
  quota: ChatQuota | null;
  loading?: boolean;
}

/**
 * 免费额度 / 积分 / 会员 实时状态条（Codex 风格顶部细条，不打扰体验）
 * - 实时显示：今日对话次数、剩余免费次数、积分余额、会员状态
 * - 额度紧张（≤20%）时柔和提示；额度用尽时给出一行升级/登录引导（不弹窗）
 */
export default function QuotaBar({ quota, loading }: QuotaBarProps) {
  const navigate = useNavigate();
  if (!quota) return null;

  const { used, limit, remaining } = quota.quota || { used: 0, limit: -1, remaining: -1 };
  const unlimited = limit === -1;
  const pct = unlimited || limit <= 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const low = !unlimited && remaining >= 0 && remaining <= Math.max(1, Math.round(limit * 0.2));
  const exhausted = !unlimited && remaining === 0;
  const member = quota.plan !== 'free' && !quota.expired;
  const planName = quota.expired ? `${PLAN_LABEL[quota.plan] || quota.plan}（已过期）` : (PLAN_LABEL[quota.plan] || quota.plan);
  const expireText = quota.membershipExpiresAt ? ` · ${new Date(quota.membershipExpiresAt).toLocaleDateString('zh-CN')}到期` : '';

  return (
    <div className={`chat-quota-bar${low ? ' quota-low' : ''}${exhausted ? ' quota-exhausted' : ''}`}>
      <Space size={14} wrap className="quota-items">
        {/* 今日对话次数 */}
        <Tooltip title={unlimited ? '会员无限次 AI 对话' : '今日 AI 对话次数（每天 0 点重置）'}>
          <span className="quota-item">
            <ThunderboltOutlined />
            <span className="quota-label">今日对话</span>
            <b>{used}</b>
            {!unlimited && <span className="quota-muted">/ {limit} 次</span>}
            {!unlimited && (
              <Progress
                percent={pct}
                showInfo={false}
                size="small"
                strokeColor={exhausted ? '#f5222d' : low ? '#faad14' : '#6366f1'}
                trailColor="rgba(148,163,184,0.18)"
                className="quota-progress"
              />
            )}
          </span>
        </Tooltip>

        {/* 剩余免费次数 */}
        {!unlimited && (
          <Tooltip title="今日剩余免费对话次数">
            <span className={`quota-item ${low ? 'quota-warn' : ''}`}>
              {low ? <WarningOutlined /> : <CheckCircleOutlined />}
              <span className="quota-label">剩余</span>
              <b>{Math.max(0, remaining)}</b>
              <span className="quota-muted">次</span>
            </span>
          </Tooltip>
        )}

        {/* 积分 */}
        <Tooltip title="可用积分（可购买/签到获取，用于解锁付费能力）">
          <span className="quota-item">
            <GiftOutlined />
            <span className="quota-label">积分</span>
            <b>{quota.credits ?? 0}</b>
          </span>
        </Tooltip>

        {/* 会员状态 */}
        <Tooltip title={member ? '当前为会员，享更高额度' : '当前为免费版，登录/升级可获得更多额度'}>
          <span className={`quota-item ${member ? 'quota-member' : ''}`}>
            <CrownOutlined />
            <span className="quota-label">会员</span>
            <b>{member ? planName : planName}</b>
            {member && expireText && <span className="quota-muted">{expireText}</span>}
          </span>
        </Tooltip>
      </Space>

      {/* 额度用尽引导：一行内联引导，不弹窗打扰 */}
      {exhausted && (
        <span className="quota-cta">
          {!quota.authenticated ? (
            <Button size="small" type="link" icon={<LoginOutlined />} onClick={() => navigate('/login?returnTo=' + encodeURIComponent('/ai-chat'))}>
              登录领取免费额度
            </Button>
          ) : (
            <Button size="small" type="link" icon={<CrownOutlined />} onClick={() => navigate('/pricing')}>
              升级套餐，解锁更多
            </Button>
          )}
          <Button size="small" type="link" icon={<GiftOutlined />} onClick={() => navigate('/points-center')}>
            积分中心
          </Button>
        </span>
      )}
    </div>
  );
}
